import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { QA } from "@/constants/testIds";
import {
  Send, FileText, Archive, Trash2, Clock, AlertTriangle, UserCheck, Tag,
  Filter, X, Pencil, Check, Settings2,
} from "lucide-react";
import Confirm from "@/components/Confirm";
import { toast } from "@/components/Toaster";
import MentionInput from "@/components/MentionInput";
import MultiAssigneePicker from "@/components/MultiAssigneePicker";

const QA_STATUS_STYLES = {
  open:     { dot: "bg-sky-500",     label: "Open",     chip: "text-sky-600 bg-sky-500/10" },
  answered: { dot: "bg-emerald-500", label: "Answered", chip: "text-emerald-600 bg-emerald-500/10" },
  archived: { dot: "bg-neutral-500", label: "Archived", chip: "text-neutral-600 dark:text-neutral-300 bg-neutral-500/15" },
};
const PRIORITY_STYLES = {
  low:    { label: "Low",     chip: "text-muted-foreground bg-secondary border-border" },
  normal: { label: "Normal",  chip: "text-foreground bg-secondary border-border" },
  high:   { label: "High",    chip: "text-amber-500 bg-amber-500/10 border-amber-500/30" },
  urgent: { label: "Urgent",  chip: "text-rose-500 bg-rose-500/10 border-rose-500/30" },
};
const SLA_OPTIONS = [
  { hours: 1,   label: "1 hour" },
  { hours: 4,   label: "4 hours" },
  { hours: 24,  label: "24 hours" },
  { hours: 72,  label: "3 days" },
  { hours: 168, label: "7 days" },
];
const SLA_FIELD_LABEL = "Preferred Response Time";

function fmtRelative(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
function fmtCountdown(dueIso) {
  if (!dueIso) return null;
  const diff = (new Date(dueIso).getTime() - Date.now()) / 1000;
  if (diff <= 0) {
    const overdueSec = -diff;
    if (overdueSec < 3600) return { overdue: true, label: `${Math.floor(overdueSec/60)}m overdue` };
    if (overdueSec < 86400) return { overdue: true, label: `${Math.floor(overdueSec/3600)}h overdue` };
    return { overdue: true, label: `${Math.floor(overdueSec/86400)}d overdue` };
  }
  if (diff < 3600) return { overdue: false, label: `due in ${Math.floor(diff/60)}m`, urgent: true };
  if (diff < 86400) return { overdue: false, label: `due in ${Math.floor(diff/3600)}h`, urgent: diff < 4*3600 };
  return { overdue: false, label: `due in ${Math.floor(diff/86400)}d`, urgent: false };
}

// Render an answer string with @mentions highlighted. We rely on the fact
// that the client inserted `@Name` tokens — pattern-match those at runtime.
function renderWithMentions(text) {
  if (!text) return null;
  const parts = [];
  const re = /(@[\w][\w .'-]{0,40})/g;
  let last = 0; let m;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <span key={m.index} className="px-1 rounded bg-primary/15 text-primary mono text-[12px]">
        {m[1]}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export default function QATab({ roomId, user }) {
  const { user: me } = useAuth();
  const isAdmin = (me?.role || user?.role) === "admin";
  const [qs, setQs] = useState([]);
  const [stats, setStats] = useState({ all: 0, open: 0, answered: 0, archived: 0, unassigned: 0, assigned_to_me: 0, overdue: 0 });
  const [members, setMembers] = useState([]);
  const [filter, setFilter] = useState("all"); // all|open|answered|archived|assigned_to_me|unassigned|overdue
  const [answers, setAnswers] = useState({});
  const [confirmState, setConfirmState] = useState(null);
  const [showAsk, setShowAsk] = useState(false);
  const [askForm, setAskForm] = useState({ text: "", priority: "normal", sla_hours: 24, assignee_ids: [], assign_all: false, tags: "" });
  const [askErr, setAskErr] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [replyMentions, setReplyMentions] = useState({}); // qid → Set<user_id>
  const [editingId, setEditingId] = useState(null); // `${qid}:${aid}` being edited
  const [editText, setEditText] = useState("");
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [highlightQid, setHighlightQid] = useState(null);
  const detailRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkQid = searchParams.get("qid");

  const load = async () => {
    const params = {};
    if (filter === "assigned_to_me") params.assignee_id = "me";
    else if (filter === "unassigned") { params.status = "open"; }
    else if (filter === "overdue") { params.only_overdue = true; params.status = "open"; }
    else if (filter !== "all") params.status = filter;
    const include_archived = isAdmin && filter === "archived";
    if (include_archived) params.include_archived = true;

    const [q, s] = await Promise.all([
      api.get(`/rooms/${roomId}/questions`, { params }),
      api.get(`/rooms/${roomId}/questions/stats`),
    ]);
    let list = q.data;
    if (filter === "unassigned") {
      list = list.filter((x) =>
        !(x.assignee_ids && x.assignee_ids.length) && !x.assignee_id && !x.assign_all,
      );
    }
    setQs(list);
    setStats(s.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [roomId, filter]);

  // Load member list for the assignee dropdowns. Admins additionally
  // pull the full users list so they can route to any platform admin even
  // if they're not yet a member of this specific room.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, u] = await Promise.all([
          api.get(`/rooms/${roomId}/members`).catch(() => ({ data: [] })),
          isAdmin ? api.get("/users").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        ]);
        if (cancelled) return;
        const map = new Map();
        for (const m1 of m.data || []) map.set(m1.user_id, m1);
        for (const u1 of u.data || []) {
          // Normalise platform-user shape into the member shape we use.
          const id = u1.id || u1._id;
          if (!map.has(id)) map.set(id, { user_id: id, name: u1.name, email: u1.email, role: u1.role });
        }
        setMembers(Array.from(map.values()));
      } catch (err) {
        // Member directory is best-effort enrichment for @mentions / assignees;
        // the QA tab still functions if /users is unreachable (e.g. non-admin).
        if (process.env.NODE_ENV !== "production") {
          console.debug("QATab members fetch failed:", err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [roomId, isAdmin]);

  const experts = useMemo(
    () => members.filter((m) => m.role === "admin" || m.role === "custom"),
    [members]
  );

  const submitAsk = async (e) => {
    e.preventDefault();
    setAskErr("");
    try {
      const tags = (askForm.tags || "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      await api.post(`/rooms/${roomId}/questions`, {
        text: askForm.text,
        priority: askForm.priority,
        sla_hours: Number(askForm.sla_hours),
        assignee_ids: askForm.assignee_ids?.length ? askForm.assignee_ids : undefined,
        assign_all: !!askForm.assign_all || undefined,
        tags,
      });
      setShowAsk(false);
      setAskForm({ text: "", priority: "normal", sla_hours: 24, assignee_ids: [], assign_all: false, tags: "" });
      await load();
      toast.success("Question posted");
    } catch (err) { setAskErr(formatError(err)); }
  };

  const answer = async (qid) => {
    const t = answers[qid];
    if (!t?.trim()) return;
    try {
      const mentions = Array.from(replyMentions[qid] || []);
      await api.post(`/rooms/${roomId}/questions/${qid}/answer`, {
        text: t, mentioned_user_ids: mentions,
      });
      setAnswers({ ...answers, [qid]: "" });
      setReplyMentions((m) => ({ ...m, [qid]: new Set() }));
      await load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const startEditAnswer = (q, a) => {
    setEditingId(`${q.id}:${a.id}`);
    setEditText(a.text || "");
  };
  const cancelEditAnswer = () => {
    setEditingId(null);
    setEditText("");
  };
  const saveEditAnswer = async (q, a) => {
    const next = editText.trim();
    if (!next || next === a.text) { cancelEditAnswer(); return; }
    try {
      await api.patch(`/rooms/${roomId}/questions/${q.id}/answer/${a.id}`, { text: next });
      cancelEditAnswer();
      await load();
      toast.success("Reply updated");
    } catch (e) { toast.error(formatError(e)); }
  };
  const deleteAnswer = (q, a) => {
    setConfirmState({
      title: "Delete reply?",
      message: "This will mark the reply as deleted. The audit trail (author, timestamp) is preserved.",
      confirmLabel: "Delete reply",
      destructive: true,
      onConfirm: async () => {
        try {
          await api.delete(`/rooms/${roomId}/questions/${q.id}/answer/${a.id}`);
          await load();
          toast.success("Reply deleted");
        } catch (e) { toast.error(formatError(e)); throw e; }
      },
    });
  };

  const setStatus = async (q, status) => {
    if (q.status === status) return;
    try {
      await api.patch(`/rooms/${roomId}/questions/${q.id}`, { status });
      toast.success(`Marked ${status}`);
      await load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const patchQuestion = async (q, patch) => {
    try {
      await api.patch(`/rooms/${roomId}/questions/${q.id}`, patch);
      await load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const removeQ = (q) => {
    setConfirmState({
      title: "Delete question?",
      message: `"${q.text.slice(0, 80)}${q.text.length > 80 ? "…" : ""}" from ${q.user_name} will be permanently deleted along with all replies. Consider archiving instead.`,
      confirmLabel: "Delete permanently",
      destructive: true,
      onConfirm: async () => {
        try {
          await api.delete(`/rooms/${roomId}/questions/${q.id}`);
          toast.success("Question deleted");
          await load();
        } catch (e) { toast.error(formatError(e)); throw e; }
      },
    });
  };

  const filterChips = [
    { key: "all",             label: "All",            count: stats.all,             admin: false },
    { key: "open",            label: "Open",           count: stats.open,            admin: false },
    { key: "answered",        label: "Answered",       count: stats.answered,        admin: false },
    { key: "assigned_to_me",  label: "Assigned to me", count: stats.assigned_to_me,  admin: true  },
    { key: "unassigned",      label: "Unassigned",     count: stats.unassigned,      admin: true  },
    { key: "overdue",         label: "Overdue",        count: stats.overdue,         admin: true  },
    { key: "archived",        label: "Archived",       count: stats.archived,        admin: true  },
  ];

  // Auto-select the first question whenever the list changes, so the
  // right-pane detail is never blank. If we arrived via a ?qid= deep-link
  // (notification "View"), prefer that question and pulse it briefly so the
  // user can see what the notification was pointing at.
  useEffect(() => {
    if (qs.length === 0) { setSelectedId(null); return; }
    if (deepLinkQid && qs.find((x) => x.id === deepLinkQid)) {
      setSelectedId(deepLinkQid);
      setHighlightQid(deepLinkQid);
      // Consume the param so a tab-refresh doesn't keep re-flashing the highlight.
      const sp = new URLSearchParams(searchParams);
      sp.delete("qid");
      setSearchParams(sp, { replace: true });
      // Scroll the detail card into view on next paint.
      requestAnimationFrame(() => {
        detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      const t = setTimeout(() => setHighlightQid(null), 2400);
      return () => clearTimeout(t);
    }
    if (!qs.find((x) => x.id === selectedId)) {
      setSelectedId(qs[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs]);

  const selected = qs.find((x) => x.id === selectedId) || qs[0] || null;

  const renderDetailCard = (q) => {
    const sStyle = QA_STATUS_STYLES[q.status] || QA_STATUS_STYLES.open;
    const pStyle = PRIORITY_STYLES[q.priority || "normal"];
    const countdown = q.status === "open" ? fmtCountdown(q.sla_due_at) : null;
    const isArchived = q.status === "archived";
    const assigneeOptions = (() => {
      const out = [...experts];
      if (q.assignee_id && !out.find((m) => m.user_id === q.assignee_id)) {
        out.push({ user_id: q.assignee_id, name: q.assignee_name, email: "", role: "admin" });
      }
      return out;
    })();
    return (
      <div
        key={q.id}
        ref={q.id === selected?.id ? detailRef : undefined}
        data-testid={`qa-row-${q.id}`}
        className={`border bg-card rounded-md p-5 transition ${
          q.overdue ? "border-rose-500/40 shadow-[inset_3px_0_0_0_hsl(var(--destructive))]" : "border-border"
        } ${isArchived ? "opacity-70" : ""} ${
          highlightQid === q.id ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""
        }`}
      >
        {/* Top row: meta — keep it lean. Hide chips that add no info
            (normal priority, SLAs far in the future, unassigned for non-admins). */}
        <div className="flex items-center flex-wrap gap-2">
          <div className="text-sm font-medium truncate mr-2">{q.user_name}</div>
          <span className={`mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${sStyle.chip}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${sStyle.dot}`} /> {sStyle.label}
          </span>
          {q.priority && q.priority !== "normal" && (
            <span className={`mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${pStyle.chip}`}>
              {pStyle.label}
            </span>
          )}
          {q.assign_all ? (
            <span className="mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 inline-flex items-center gap-1">
              <UserCheck className="w-3 h-3" /> All admins
            </span>
          ) : (q.assignee_names?.length || q.assignee_name) ? (
            <span className="mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary inline-flex items-center gap-1"
              title={(q.assignee_names || [q.assignee_name]).join(", ")}
            >
              <UserCheck className="w-3 h-3" />
              {(() => {
                const names = q.assignee_names && q.assignee_names.length
                  ? q.assignee_names : (q.assignee_name ? [q.assignee_name] : []);
                if (names.length === 1) return names[0];
                if (names.length === 2) return names.join(" & ");
                return `${names[0]} +${names.length - 1}`;
              })()}
            </span>
          ) : null}
          {!q.assign_all && !(q.assignee_names?.length) && !q.assignee_name && isAdmin && q.status === "open" && (
            <span className="mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500">
              Unassigned
            </span>
          )}
          {/* SLA chip only shown when meaningful — overdue, urgent, or due soon. */}
          {countdown && (countdown.overdue || countdown.urgent) && (
            <span className={`mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${
              countdown.overdue ? "bg-rose-500/10 text-rose-500" : "bg-amber-500/10 text-amber-500"
            }`}>
              {countdown.overdue ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
              {countdown.label}
            </span>
          )}
          {(q.tags || []).slice(0, 3).map((t) => (
            <span key={t} className="mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-secondary text-muted-foreground inline-flex items-center gap-1">
              <Tag className="w-2.5 h-2.5" /> {t}
            </span>
          ))}
          {(q.tags || []).length > 3 && (
            <span className="mono text-[10px] text-muted-foreground">+{q.tags.length - 3}</span>
          )}
          <span className="ml-auto mono text-[10px] text-muted-foreground whitespace-nowrap">{fmtRelative(q.created_at)}</span>
          {isAdmin && (
            <button
              type="button"
              data-testid={`qa-admin-toggle-${q.id}`}
              onClick={() => setAdminPanelOpen((v) => !v)}
              title="Admin controls"
              className={`p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition ${adminPanelOpen ? "bg-secondary text-foreground" : ""}`}
            >
              <Settings2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <h4 className="font-display text-lg mt-3 whitespace-pre-wrap">{q.text}</h4>

        {(q.doc_id || q.page) && (
          <div className="mt-2">
            {q.doc_id ? (
              <Link
                to={`/app/rooms/${roomId}/view/${q.doc_id}${q.page ? `?page=${q.page}` : ""}`}
                className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition"
              >
                <FileText className="w-3 h-3" /> {q.page ? `Page ${q.page} · ` : ""}Jump to document
              </Link>
            ) : (
              <span className="mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-md bg-secondary">Page {q.page}</span>
            )}
          </div>
        )}

        {/* Thread */}
        {(q.answers || []).length > 0 && (
          <div className="mt-4 space-y-3 border-l-2 border-primary pl-4">
            {q.answers.map((a, i) => {
              const myId = me?.id || user?.id;
              const isMine = a.user_id === myId;
              // Only the author can edit/delete their own reply. Admins still
              // have other tools (archive question, delete the whole thread).
              const canModify = isMine && !a.deleted;
              const isEditing = editingId === `${q.id}:${a.id}`;
              return (
                <div key={a.id || i} className="group">
                  <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-foreground">{a.user_name}</span>
                    <span className="mono">· {new Date(a.ts).toLocaleString()}</span>
                    {a.edited && !a.deleted && (
                      <span className="mono text-[10px] text-muted-foreground italic">(edited)</span>
                    )}
                    {a.deleted && (
                      <span className="mono text-[10px] text-muted-foreground italic">(deleted)</span>
                    )}
                    {canModify && a.id && !isEditing && (
                      <span className="ml-auto inline-flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
                        <button
                          type="button"
                          data-testid={`qa-answer-edit-${a.id}`}
                          onClick={() => startEditAnswer(q, a)}
                          title="Edit reply"
                          className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          data-testid={`qa-answer-delete-${a.id}`}
                          onClick={() => deleteAnswer(q, a)}
                          title="Delete reply"
                          className="p-1 rounded hover:bg-rose-500/10 text-muted-foreground hover:text-rose-500 transition"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                  </div>
                  {a.deleted ? (
                    <div className="text-sm mt-1 italic text-muted-foreground">
                      [Reply removed by author]
                    </div>
                  ) : isEditing ? (
                    <div className="mt-1 space-y-2">
                      <textarea
                        data-testid={`qa-answer-edit-input-${a.id}`}
                        autoFocus
                        rows={2}
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") { e.preventDefault(); cancelEditAnswer(); }
                          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); saveEditAnswer(q, a); }
                        }}
                        className="w-full px-3 py-2 bg-background border border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm resize-none"
                      />
                      <div className="flex items-center justify-end gap-2">
                        <span className="mr-auto mono text-[10px] text-muted-foreground">⌘+Enter to save · Esc to cancel</span>
                        <button
                          type="button"
                          onClick={cancelEditAnswer}
                          className="px-2.5 py-1 text-xs rounded-md border border-border hover:bg-secondary transition"
                        >Cancel</button>
                        <button
                          type="button"
                          data-testid={`qa-answer-save-${a.id}`}
                          onClick={() => saveEditAnswer(q, a)}
                          className="px-2.5 py-1 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition inline-flex items-center gap-1"
                        >
                          <Check className="w-3 h-3" /> Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm mt-1 whitespace-pre-wrap">{renderWithMentions(a.text)}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!isArchived && (
          <div className="mt-4">
            <MentionInput
              testid={QA.answerInput}
              value={answers[q.id] || ""}
              mentions={replyMentions[q.id] || new Set()}
              onChange={(text, mset) => {
                setAnswers({ ...answers, [q.id]: text });
                setReplyMentions((m) => ({ ...m, [q.id]: mset }));
              }}
              onSubmit={() => answer(q.id)}
              members={members}
              placeholder="Reply… Use @ to mention a member"
            />
            <div className="mt-2 flex items-center justify-between">
              <span className="mono text-[10px] text-muted-foreground">
                Enter to send · Shift+Enter for newline · @ to mention
              </span>
              <button
                data-testid={QA.submitAnswer}
                onClick={() => answer(q.id)}
                className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition text-xs"
              >
                Reply
              </button>
            </div>
          </div>
        )}

        {isAdmin && adminPanelOpen && (
          <div className="mt-4 pt-4 border-t border-border space-y-3" data-testid={`qa-admin-panel-${q.id}`}>
            <div>
              <span className="label-mono text-xs">Assignees</span>
              <div className="mt-1">
                <MultiAssigneePicker
                  options={assigneeOptions.map((m) => ({
                    user_id: m.user_id, name: m.name, email: m.email, role: m.role,
                  }))}
                  selectedIds={q.assignee_ids || (q.assignee_id ? [q.assignee_id] : [])}
                  assignAll={!!q.assign_all}
                  testId={`qa-assignees-${q.id}`}
                  dense
                  placeholder="Add assignee or pick All admins…"
                  onChange={({ ids, assignAll }) => patchQuestion(q, {
                    assignee_ids: ids, assign_all: assignAll,
                  })}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              <label className="block">
                <span className="label-mono">{SLA_FIELD_LABEL}</span>
                <select
                  data-testid={`qa-sla-${q.id}`}
                  value={q.sla_hours || 24}
                  onChange={(e) => patchQuestion(q, { sla_hours: Number(e.target.value) })}
                  className="mt-1 w-full px-2 py-1.5 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-xs"
                >
                  {SLA_OPTIONS.map((s) => (
                    <option key={s.hours} value={s.hours}>{s.label}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="label-mono">Priority</span>
                <select
                  data-testid={`qa-priority-${q.id}`}
                  value={q.priority || "normal"}
                  onChange={(e) => patchQuestion(q, { priority: e.target.value })}
                  className="mt-1 w-full px-2 py-1.5 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-xs"
                >
                  {Object.entries(PRIORITY_STYLES).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="label-mono text-muted-foreground mr-1">— Admin</span>
              <button onClick={() => setStatus(q, "open")} disabled={q.status === "open"}
                className={`px-2.5 py-1 text-xs rounded-md border transition ${q.status === "open" ? "border-sky-500 text-sky-600 bg-sky-500/10" : "border-border hover:bg-secondary"} disabled:cursor-default`}
              >Open</button>
              <button onClick={() => setStatus(q, "answered")} disabled={q.status === "answered"}
                className={`px-2.5 py-1 text-xs rounded-md border transition ${q.status === "answered" ? "border-emerald-500 text-emerald-600 bg-emerald-500/10" : "border-border hover:bg-secondary"} disabled:cursor-default`}
              >Mark answered</button>
              <button onClick={() => setStatus(q, "archived")} disabled={q.status === "archived"}
                className={`px-2.5 py-1 text-xs rounded-md border transition ${q.status === "archived" ? "border-neutral-500 text-neutral-600 dark:text-neutral-300 bg-neutral-500/10" : "border-border hover:bg-secondary"} disabled:cursor-default inline-flex items-center gap-1.5`}
              >
                <Archive className="w-3 h-3" /> Archive
              </button>
              <button onClick={() => removeQ(q)}
                className="ml-auto px-2.5 py-1 text-xs rounded-md text-rose-600 hover:bg-destructive hover:text-destructive-foreground transition inline-flex items-center gap-1.5"
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderListRow = (q) => {
    const sStyle = QA_STATUS_STYLES[q.status] || QA_STATUS_STYLES.open;
    const pStyle = PRIORITY_STYLES[q.priority || "normal"];
    const countdown = q.status === "open" ? fmtCountdown(q.sla_due_at) : null;
    const isSel = q.id === selectedId;
    return (
      <button
        key={q.id}
        type="button"
        data-testid={`qa-list-${q.id}`}
        onClick={() => setSelectedId(q.id)}
        className={`w-full text-left px-3 py-3 border-b border-border last:border-b-0 transition relative ${
          isSel ? "bg-primary/[0.06]" : "hover:bg-secondary/40"
        } ${q.overdue ? "border-l-[3px] border-l-rose-500" : ""} ${
          highlightQid === q.id ? "ring-2 ring-primary ring-inset" : ""
        }`}
      >
        <div className="flex items-center gap-2 mb-1">
          <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${sStyle.dot}`} />
          <span className="text-sm font-medium truncate flex-1">{q.user_name}</span>
          <span className="mono text-[10px] text-muted-foreground whitespace-nowrap">{fmtRelative(q.created_at)}</span>
        </div>
        <div className="text-sm truncate text-foreground/90">{q.text}</div>
        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
          {q.priority && q.priority !== "normal" && (
            <span className={`mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${pStyle.chip}`}>{pStyle.label}</span>
          )}
          {q.assign_all ? (
            <span className="mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-500 inline-flex items-center gap-1">
              <UserCheck className="w-2.5 h-2.5" /> All admins
            </span>
          ) : (q.assignee_names?.length || q.assignee_name) ? (
            <span className="mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary inline-flex items-center gap-1"
              title={(q.assignee_names || [q.assignee_name]).join(", ")}
            >
              <UserCheck className="w-2.5 h-2.5" />
              {(() => {
                const names = q.assignee_names && q.assignee_names.length
                  ? q.assignee_names : (q.assignee_name ? [q.assignee_name] : []);
                const first = names[0]?.split(" ")[0] || "";
                return names.length > 1 ? `${first} +${names.length - 1}` : first;
              })()}
            </span>
          ) : (
            <span className="mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500">Unassigned</span>
          )}
          {countdown && (
            <span className={`mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${
              countdown.overdue ? "bg-rose-500/10 text-rose-500" : countdown.urgent ? "bg-amber-500/10 text-amber-500" : "bg-emerald-500/10 text-emerald-500"
            }`}>
              {countdown.overdue ? <AlertTriangle className="w-2.5 h-2.5" /> : <Clock className="w-2.5 h-2.5" />}
              {countdown.label}
            </span>
          )}
          {(q.answers || []).length > 0 && (
            <span className="mono text-[9px] text-muted-foreground">{q.answers.length} {q.answers.length === 1 ? "reply" : "replies"}</span>
          )}
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="label-mono text-muted-foreground inline-flex items-center gap-2">
            <Filter className="w-3.5 h-3.5" /> — Q&amp;A inbox
          </div>
          <h3 className="font-display text-2xl tracking-tight mt-1">Structured Q&amp;A</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Route each question to a subject-matter expert, set a preferred response time,
            and keep every reply inside the room with a full audit trail.
          </p>
        </div>
        <button
          data-testid={QA.newQuestion + "-open"}
          onClick={() => setShowAsk(true)}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition shrink-0"
        >
          <Send className="w-4 h-4" /> Ask question
        </button>
      </div>

      {/* Quick stats */}
      {isAdmin && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Open"           value={stats.open}            tone="text-sky-500 bg-sky-500/10"     Icon={Filter} />
          <Tile label="Assigned to me" value={stats.assigned_to_me}  tone="text-primary bg-primary/10"    Icon={UserCheck} />
          <Tile label="Unassigned"     value={stats.unassigned}      tone="text-amber-500 bg-amber-500/10" Icon={AlertTriangle} warn={stats.unassigned > 0} />
          <Tile label="Overdue"        value={stats.overdue}         tone="text-rose-500 bg-rose-500/10"   Icon={Clock}         warn={stats.overdue > 0} />
        </div>
      )}

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        {filterChips
          .filter((f) => !f.admin || isAdmin)
          .map((f) => (
            <button
              key={f.key}
              data-testid={`qa-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-xs rounded-full border transition ${
                filter === f.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"
              }`}
            >
              {f.label} <span className="opacity-70 ml-1 tabular-nums">{f.count}</span>
            </button>
          ))}
      </div>

      {/* Two-pane: list on the left, selected detail on the right. On
          narrower screens (lg-) it collapses to a single column. */}
      <div className="grid grid-cols-1 lg:grid-cols-[360px_minmax(0,1fr)] gap-4">
        <aside className="border border-border bg-card rounded-md overflow-hidden">
          <div className="px-3 py-2 border-b border-border label-mono text-muted-foreground flex items-center justify-between">
            <span>{qs.length} thread{qs.length === 1 ? "" : "s"}</span>
            {qs.length > 0 && <span className="mono text-[10px]">Tap to open</span>}
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {qs.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-muted-foreground">
                No questions match this filter.
              </div>
            ) : qs.map(renderListRow)}
          </div>
        </aside>
        <section className="min-w-0">
          {selected ? renderDetailCard(selected) : (
            <div className="border border-dashed border-border rounded-md p-12 text-center text-sm text-muted-foreground">
              Select a thread on the left to view its replies.
            </div>
          )}
        </section>
      </div>

      {/* Ask question modal */}
      {showAsk && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setShowAsk(false)}>
          <form
            className="bg-card border border-border rounded-md w-full max-w-lg p-6 space-y-4 relative"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitAsk}
          >
            <button
              type="button"
              aria-label="Close"
              onClick={() => setShowAsk(false)}
              className="absolute top-3 right-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition"
            >
              <X className="w-4 h-4" />
            </button>
            <h3 className="font-display text-xl tracking-tight">Ask a question</h3>
            <textarea
              data-testid={QA.newQuestion}
              required
              value={askForm.text}
              onChange={(e) => setAskForm({ ...askForm, text: e.target.value })}
              rows={3}
              placeholder="Be specific — link the document or page if relevant…"
              className="w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none text-sm"
            />
            <div className="grid grid-cols-2 gap-3">
              <label className="block col-span-2">
                <span className="label-mono">Route to expert(s)</span>
                <div className="mt-1">
                  <MultiAssigneePicker
                    options={experts.map((m) => ({
                      user_id: m.user_id, name: m.name, email: m.email, role: m.role,
                    }))}
                    selectedIds={askForm.assignee_ids || []}
                    assignAll={!!askForm.assign_all}
                    testId="ask-assignees"
                    placeholder="Pick one or more experts — or leave blank for any admin…"
                    onChange={({ ids, assignAll }) => setAskForm({
                      ...askForm, assignee_ids: ids, assign_all: assignAll,
                    })}
                  />
                </div>
              </label>
              <label className="block">
                <span className="label-mono">{SLA_FIELD_LABEL}</span>
                <select
                  value={askForm.sla_hours}
                  onChange={(e) => setAskForm({ ...askForm, sla_hours: e.target.value })}
                  className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                >
                  {SLA_OPTIONS.map((s) => (
                    <option key={s.hours} value={s.hours}>{s.label}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="label-mono">Priority</span>
                <select
                  value={askForm.priority}
                  onChange={(e) => setAskForm({ ...askForm, priority: e.target.value })}
                  className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                >
                  {Object.entries(PRIORITY_STYLES).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="label-mono">Tags <span className="opacity-60">(comma-sep)</span></span>
                <input
                  value={askForm.tags}
                  onChange={(e) => setAskForm({ ...askForm, tags: e.target.value })}
                  placeholder="financial, opex"
                  className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                />
              </label>
            </div>
            {askErr && <div className="text-xs text-destructive">{askErr}</div>}
            <button
              data-testid={QA.submitQuestion}
              type="submit"
              className="w-full px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition inline-flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" /> Post question
            </button>
          </form>
        </div>
      )}

      <Confirm state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}

function Tile({ label, value, tone, warn, Icon }) {
  const IconCmp = Icon || Clock;
  return (
    <div
      data-testid={`qa-stat-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}
      className={`border bg-card rounded-md p-3 flex items-center gap-3 min-w-0 ${warn ? "border-rose-500/30" : "border-border"}`}
    >
      <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${tone}`}>
        <IconCmp className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="font-display text-xl tracking-tight tabular-nums truncate">{value}</div>
        <div className="label-mono text-muted-foreground truncate">{label}</div>
      </div>
    </div>
  );
}
